import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type { EmailRequest, EmailTemplate } from '@/types/userCourseProgress.types';

class EmailService {
  /**
   * Send email to a user regarding course progress
   */
  async sendEmail(emailData: EmailRequest): Promise<{
    messageId: string;
    status: string;
    sentAt: string;
    recipient: string;
    trackingId: string;
  }> {
    try {
      const response = await apiClient.post<{
        status: string;
        data: {
          messageId: string;
          status: string;
          sentAt: string;
          recipient: string;
          trackingId: string;
        };
        timestamp: string;
        description: string;
      }>(API_ENDPOINTS.EMAIL_COMMUNICATION.SEND, emailData);

      if (response.data.status === 'success') {
        return response.data.data;
      }
      throw new Error('Failed to send email');
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error('Email rate limit exceeded. Maximum 10 emails per day per recipient.');
      }
      console.error('Error sending email:', error);
      throw error;
    }
  }

  /**
   * Get available email templates
   */
  async getEmailTemplates(): Promise<EmailTemplate[]> {
    try {
      const response = await apiClient.get<{
        status: string;
        data: EmailTemplate[];
        timestamp: string;
        description: string;
      }>(API_ENDPOINTS.EMAIL_COMMUNICATION.GET_TEMPLATES);

      if (response.data.status === 'success') {
        return response.data.data;
      }
      throw new Error('Failed to fetch email templates');
    } catch (error) {
      console.error('Error fetching email templates:', error);
      throw error;
    }
  }

  /**
   * Helper method to create encouragement email
   */
  createEncouragementEmail(
    recipientUserId: string,
    recipientEmail: string,
    senderUserId: string,
    courseTitle: string,
    currentProgress: number
  ): EmailRequest {
    return {
      recipientUserId,
      recipientEmail,
      senderUserId,
      subject: `Great progress on ${courseTitle}!`,
      templateType: 'encouragement',
      content: {
        body: `Keep up the excellent work! You're ${currentProgress}% complete with the course.`,
        isHtml: false
      },
      context: {
        courseTitle,
        currentProgress
      },
      metadata: {
        category: 'progress_update',
        priority: 'normal',
        trackingEnabled: true
      }
    };
  }

  /**
   * Helper method to create reminder email
   */
  createReminderEmail(
    recipientUserId: string,
    recipientEmail: string,
    senderUserId: string,
    courseTitle: string,
    currentProgress: number,
    daysUntilDeadline: number,
    deadline: string
  ): EmailRequest {
    return {
      recipientUserId,
      recipientEmail,
      senderUserId,
      subject: `Course Deadline Reminder - ${courseTitle}`,
      templateType: 'reminder',
      content: {
        body: `You're ${currentProgress}% complete with "${courseTitle}". The deadline is ${deadline} (${daysUntilDeadline} days remaining). Let me know if you need any help.`,
        isHtml: false
      },
      context: {
        courseTitle,
        currentProgress,
        daysUntilDeadline
      },
      metadata: {
        category: 'deadline_reminder',
        priority: daysUntilDeadline <= 3 ? 'high' : 'normal',
        trackingEnabled: true
      }
    };
  }

  /**
   * Helper method to create check-in email
   */
  createCheckInEmail(
    recipientUserId: string,
    recipientEmail: string,
    senderUserId: string,
    courseTitle: string,
    lastActiveDate?: string
  ): EmailRequest {
    return {
      recipientUserId,
      recipientEmail,
      senderUserId,
      subject: `How's your learning journey going?`,
      templateType: 'check_in',
      content: {
        body: `I noticed you haven't accessed "${courseTitle}" recently${lastActiveDate ? ` (last active: ${lastActiveDate})` : ''}. Is there anything I can help you with?`,
        isHtml: false
      },
      context: {
        courseTitle
      },
      metadata: {
        category: 'engagement',
        priority: 'normal',
        trackingEnabled: true
      }
    };
  }

  /**
   * Helper method to create custom email
   */
  createCustomEmail(
    recipientUserId: string,
    recipientEmail: string,
    senderUserId: string,
    subject: string,
    body: string,
    context?: {
      courseId?: string;
      courseTitle?: string;
      currentProgress?: number;
      daysUntilDeadline?: number;
      currentGrade?: number;
    }
  ): EmailRequest {
    return {
      recipientUserId,
      recipientEmail,
      senderUserId,
      subject,
      templateType: 'custom',
      content: {
        body,
        isHtml: false
      },
      context,
      metadata: {
        category: 'progress_update',
        priority: 'normal',
        trackingEnabled: true
      }
    };
  }
}

export const emailService = new EmailService();